import * as React from 'react'

import { cn } from './cn'

/** Layout-only stand-ins for the old MUI form wrappers. */

export const FormControl = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    fullWidth?: boolean
    size?: 'small' | 'medium'
    margin?: 'none' | 'dense' | 'normal'
    error?: boolean
    disabled?: boolean
    variant?: string
    component?: React.ElementType
  }
>(
  (
    {
      fullWidth,
      size,
      margin,
      error,
      disabled,
      variant,
      component: Component = 'div',
      className,
      ...props
    },
    ref,
  ) => (
    <Component
      ref={ref}
      className={cn(
        'inline-flex flex-col align-top',
        fullWidth && 'w-full',
        margin === 'normal' && 'my-4',
        margin === 'dense' && 'my-2',
        disabled && 'opacity-60',
        className,
      )}
      {...props}
    />
  ),
)
FormControl.displayName = 'FormControl'

export const FormGroup = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { row?: boolean }
>(({ row, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex', row ? 'flex-row flex-wrap' : 'flex-col', className)}
    {...props}
  />
))
FormGroup.displayName = 'FormGroup'

export const InputLabel = React.forwardRef<
  HTMLLabelElement,
  React.LabelHTMLAttributes<HTMLLabelElement> & {
    size?: 'small' | 'normal'
    shrink?: boolean
    error?: boolean
  }
>(({ size, shrink, error, className, ...props }, ref) => (
  <label
    ref={ref}
    className={cn(
      'mb-1.5 block text-[13px] font-medium',
      error ? 'text-error' : 'text-text-secondary',
      className,
    )}
    {...props}
  />
))
InputLabel.displayName = 'InputLabel'

export const FormLabel = InputLabel

export interface FormControlLabelProps
  extends Omit<React.LabelHTMLAttributes<HTMLLabelElement>, 'control'> {
  control: React.ReactElement
  label?: React.ReactNode
  labelPlacement?: 'end' | 'start' | 'top' | 'bottom'
  disabled?: boolean
}

export const FormControlLabel = React.forwardRef<
  HTMLLabelElement,
  FormControlLabelProps
>(
  (
    { control, label, labelPlacement = 'end', disabled, className, ...props },
    ref,
  ) => (
    <label
      ref={ref}
      className={cn(
        'inline-flex cursor-pointer items-center gap-2 select-none',
        labelPlacement === 'start' && 'flex-row-reverse',
        labelPlacement === 'top' && 'flex-col-reverse',
        labelPlacement === 'bottom' && 'flex-col',
        disabled && 'pointer-events-none opacity-50',
        className,
      )}
      {...props}
    >
      {control}
      {label != null && label !== '' && (
        <span className="text-sm text-text-primary">{label}</span>
      )}
    </label>
  ),
)
FormControlLabel.displayName = 'FormControlLabel'

export const FormHelperText = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement> & { error?: boolean }
>(({ error, className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      'mx-1 mt-1 text-xs',
      error ? 'text-error' : 'text-text-secondary',
      className,
    )}
    {...props}
  />
))
FormHelperText.displayName = 'FormHelperText'
